import { useIntl } from 'react-intl';
import useColumnWidthsChange from 'src/componentes/list/utils/use-column-widths-change';
import LoadingContainer from 'src/componentes/loading-container';
import ListColumn from 'src/componentes/NewList/ListColumn';
import { getColumns, getFilter } from 'src/componentes/NewList';
import Results from 'src/componentes/NewList/Results';
import Toolbar from 'src/componentes/NewList/Toolbar';
import { capitalizeFirstLetter } from 'src/utils/string';
import { useState, useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import errorHandler from 'src/utils/error-handler';
import http from 'src/services/httpService';
import { COLUNAS, FILTROS } from 'src/utils/constants';
import { useSelector } from 'react-redux';
import DistribuicaoCopiasControladasDrawer from './DistribuicaoCopiasControladasDrawer';
import SearchSchema from './DistribuicaoCopiasControladasSearchSchema';

function DistribuicaoCopiasControladas({
  documentoId,
  refreshTrigger = 0,
  enabled = true,
}) {
  const resources = useSelector((state) => state.user.termos);
  const [searchText, setSearchText] = useState('');
  const [copiasControladas, setCopiasControladas] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const handleColumnWidthsChange = useColumnWidthsChange(
    setColumns,
    setAllColumns
  );
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState(null);

  const intl = useIntl();

  const loadCopiasControladas = useCallback(
    async (currentSearchText = '') => {
      if (!documentoId || !enabled) return;

      try {
        setIsLoading(true);

        const savedFilter = await getFilter(
          FILTROS.COPIAS_CONTROLADAS_DISTRIBUIDAS,
          true
        );
        setFilter(savedFilter);

        const filterValues = savedFilter?.valor ?? {};
        const response = await http.post('/DocumentoCopiasControladas/List', {
          model: {
            documentoId,
            searchText: currentSearchText,
            colaboradores: filterValues.colaboradores ?? null,
            areas: filterValues.areas ?? null,
            considerarAreasSubordinadas:
              filterValues.considerarAreasSubordinadas ?? null,
            dataCopia: filterValues.dataCopia ?? null,
            numeroCopia: filterValues.numeroCopia ?? null,
            codigoVersao: filterValues.codigoVersao ?? null,
          },
          sort: 'dataSolicitacao desc',
          pageSize,
          page: currentPage - 1,
          parameters: {
            documentoId,
          },
        });

        setCopiasControladas(response?.data?.list || []);
        setCount(response?.data?.count ?? 0);
      } catch (err) {
        errorHandler(err);
      } finally {
        setIsLoading(false);
      }
    },
    [documentoId, pageSize, currentPage, enabled]
  );

  const renderJustificativa = ({ justificativa }) =>
    justificativa ? (
      <div
        title={justificativa}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 300,
        }}
      >
        {justificativa}
      </div>
    ) : (
      ''
    );

  const defaultColumns = useMemo(
    () => [
      <ListColumn
        key="nome"
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'colaborador' },
            {
              colaborador: resources?.colaborador,
            }
          )
        )}
        valueField="nome"
        default
      />,
      <ListColumn
        key="arquivo"
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'Arquivo' })
        )}
        valueField="arquivo"
        valueFunction={(item) => {
          return item?.arquivo?.nomeOriginal ?? '-';
        }}
        required
        default
      />,
      <ListColumn
        key="area"
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'labelAreaDestino' },
            {
              area: resources?.area,
            }
          )
        )}
        valueField="area"
        default
      />,
      <ListColumn
        key="dataSolicitacao"
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'labelDataCopia' })
        )}
        valueField="dataSolicitacao"
        valueFunction={({ dataSolicitacao }) =>
          dataSolicitacao
            ? moment(dataSolicitacao).format('DD/MM/YYYY HH:mm')
            : ''
        }
        default
      />,
      <ListColumn
        key="numeroCopia"
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'labelNumeroCopia' })
        )}
        valueField="numeroCopia"
        default
      />,
      <ListColumn
        key="codigoVersao"
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'labelCodigoVersao' })
        )}
        valueField="codigoVersao"
        default
      />,
      <ListColumn
        key="justificativa"
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'justificativa' })
        )}
        valueField="justificativa"
        valueFunction={renderJustificativa}
        default
      />,
    ],
    [intl, resources]
  );

  const loadColumns = useCallback(async () => {
    const result = await getColumns(
      defaultColumns,
      null,
      COLUNAS.COPIAS_CONTROLADAS_DISTRIBUIDAS
    );
    if (result) {
      setColumns(result.columns);
      setAllColumns(result.allColumns);
    }
  }, [defaultColumns]);

  useEffect(() => {
    loadColumns();
  }, [loadColumns]);

  const handleColumnsChange = useCallback(async () => {
    try {
      setIsLoading(true);
      await loadColumns();
    } finally {
      setIsLoading(false);
    }
  }, [loadColumns]);

  const handleSearchChange = useCallback((value) => {
    setCurrentPage(1);
    setSearchText(value);
  }, []);

  const handleChangeCurrentPage = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleChangePageSize = useCallback((newSize) => {
    setCurrentPage(1);
    setPageSize(newSize);
  }, []);

  const handleRefresh = useCallback(() => {
    if (currentPage === 1) {
      loadCopiasControladas(searchText);
    } else {
      setCurrentPage(1);
    }
  }, [loadCopiasControladas, searchText, currentPage]);

  useEffect(() => {
    loadCopiasControladas(searchText);
  }, [loadCopiasControladas, searchText, refreshTrigger]);

  return (
    <LoadingContainer isLoading={isLoading}>
      <Toolbar
        showSearch
        columnsTag={COLUNAS.COPIAS_CONTROLADAS_DISTRIBUIDAS}
        filterTag={FILTROS.COPIAS_CONTROLADAS_DISTRIBUIDAS}
        onSearchChange={handleSearchChange}
        onRefresh={handleRefresh}
        searchText={searchText}
        isLoading={isLoading}
        filterDrawer
        useSavedFilter
        filter={filter}
        searchSchema={SearchSchema}
        searchComponent={<DistribuicaoCopiasControladasDrawer />}
        columns={columns}
        allColumns={allColumns}
        onColumnsChange={handleColumnsChange}
      />
      <Results
        columnsTag={COLUNAS.COPIAS_CONTROLADAS_DISTRIBUIDAS}
        onColumnsChange={handleColumnWidthsChange}
        columns={columns}
        allColumns={allColumns}
        data={copiasControladas}
        count={count}
        currentPage={currentPage}
        pageSize={pageSize}
        onChangeCurrentPage={handleChangeCurrentPage}
        onChangePageSize={handleChangePageSize}
        handleRefreshPage={handleRefresh}
      />
    </LoadingContainer>
  );
}

export default DistribuicaoCopiasControladas;
